import { useRef, useState } from 'react'
import clsx from 'clsx'

import { CONCEPT_NAME, TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { types } from '../../../../shared/eventEmitter'
import { blobTypes, downloadFile, exportFileName, mapCompanyTypeToName, stateLabel } from '../../../../shared/helpers'
import { useIsReadOnly } from '../../../../shared/alumni'
import useScrollTopOnTabChange from '../../../../shared/useScrollTopOnTabChange'

import BaseModal from '../../../../UI/BaseModal/BaseModal'
import Btn from '../../../../UI/Btn'
import Icon from '../../../../UI/Icon'
import Loading from '../../../../UI/Loading'
import TabBar from '../../../../UI/TabBar'

import ScoreField from '../../common/Concept/ScoreField'
import CourseStanding from '../../common/CourseStanding'
import ConceptOverview from './ConceptOverview'

import styles from './dialog.module.scss'
import { formatDate } from '../../../../shared/datetime'

/**
 * Das Konzept hinter einer Zeile der Liste — zum Lesen.
 *
 * Die Auswertung zeigt Zahlen: bestandene Kapitel, Stand, Score. Was dahinter
 * steht, stand bisher nur der Junior Company und ihrer Lehrkraft offen. Ein
 * Klick auf die Zeile öffnet hier genau das: die fünfzehn Konzeptfelder mit
 * ihrem Text und ihrer Bewertung, dazu die Lernstrecke der angeklickten Person.
 *
 * **Vier Reiter statt einer Bahn.** Alles untereinander waren gut zwei
 * Bildschirmhöhen, durch die man scrollen musste, um von der Frage „wo steht
 * die Junior Company?" zur Antwort zu kommen — die fünfzehn Feldtexte allein
 * füllen den größten Teil davon. Die vier Reiter trennen die vier Fragen, mit
 * denen dieses Fenster geöffnet wird:
 *
 * 1. **Übersicht** — wo steht das Konzept, wer ist am Zug, wie ist bewertet,
 * 2. **Schüler:innen** — wer gehört dazu, wie weit ist wer,
 * 3. **{@link CONCEPT_NAME}** — die fünfzehn Konzeptfelder mit Text und Punkten,
 * 4. **Die Junior Company** — Unternehmensform, Schule, Lehrkraft, Bundesland.
 *
 * Der Kopf steht über den Reitern und bleibt: Welche Junior Company und über
 * wen geöffnet — die Zuordnung darf beim Reiterwechsel nicht verloren gehen.
 *
 * **Nur lesen.** Bewerten, zurückgeben und von der Quizpflicht befreien bleibt
 * bei der betreuenden Lehrkraft — die Administration schaut zu. Deshalb steht
 * hier kein einziger Knopf, der etwas ändert; die einzige Ausnahme im Konzept,
 * die der Administration zusteht (die Freigabe im Störfall), hat ihren eigenen
 * Weg.
 *
 * Das Konzept gehört der **Junior Company**, nicht der einzelnen Person: Alle
 * schreiben an denselben fünfzehn Feldern. Die angeklickte Person steht deshalb
 * oben als Anlass und ist in den Reitern an ihren Feldern markiert — sonst läse
 * man das Fenster als „ihr Konzept", was es nicht ist.
 */

/*
 * `started: false` misst die **fünfzehn Konzeptfelder** — Texte, Punkte,
 * Zuteilungen (`conceptStarted`). Der Server legt dafuer bewusst nichts an,
 * sonst waere aus „Nicht begonnen" allein durch das Hinsehen „In Arbeit"
 * geworden.
 *
 * **Die Lernstrecke zaehlt hier nicht mit**, und der Satz sagt das jetzt auch.
 * Vorher stand da „hat das Konzept bisher nicht geoeffnet" — eine Aussage
 * ueber das Aufrufen der Seite, die so gar nicht gemessen wird. Seit der
 * Fertigstellungsgrad daneben steht, war sie ausserdem sichtbar falsch: Eine
 * Junior Company, deren Schueler:innen alle zehn Kapitel bestanden haben,
 * stand als „nie geoeffnet" da und daneben bei 25 %.
 */
const NICHT_BEGONNEN = 'Noch nicht begonnen — an den fünfzehn Konzeptfeldern wurde bisher nicht gearbeitet.'

const STATUS_TEXT = {
	DRAFT: 'In Arbeit — die Schüler:innen schreiben noch.',
	SUBMITTED: 'Zur Bewertung abgeschlossen — liegt bei der Lehrkraft.',
	RETURNED: 'Von der Lehrkraft zur Überarbeitung zurückgegeben.',
	SCORED: 'Bewertet.'
}

const TAB = {
	UEBERSICHT: 'uebersicht',
	SCHUELER: 'schueler',
	KONZEPT: 'konzept',
	COMPANY: 'company'
}

/**
 * Fortlaufende Nummern zu Bereichen zusammenziehen: 1–15 statt fünfzehn
 * Zahlen mit Komma. Wer an allen Konzeptfeldern gearbeitet hat, soll dafür nicht
 * eine Zeile Zahlen bekommen, in der die Lücke ohnehin nicht auffiele.
 */
const alsBereiche = nummern => {
	const sortiert = [...nummern].sort((a, b) => a - b)
	const teile = []

	sortiert.forEach((nr, index) => {
		const letzter = teile[teile.length - 1]
		if (index > 0 && nr === sortiert[index - 1] + 1) letzter[1] = nr
		else teile.push([nr, nr])
	})

	// Zwei benachbarte Zahlen als „3–4" zu schreiben spart nichts und liest
	// sich schlechter als „3, 4".
	return teile
		.flatMap(([von, bis]) => (bis - von >= 2 ? [`${von}–${bis}`] : von === bis ? [von] : [von, bis]))
		.join(', ')
}

const datum = wert => formatDate(wert, { day: '2-digit', month: '2-digit', year: 'numeric' }) || null

export default function ConceptDialog() {
	const [zeile, setZeile] = useState(null)
	const [daten, setDaten] = useState(null)
	const [laedt, setLaedt] = useState(false)
	const [holtPdf, setHoltPdf] = useState(false)
	const readOnly = useIsReadOnly()
	const [tab, setTab] = useState(TAB.UEBERSICHT)

	// Beim Reiterwechsel an den Anfang: Wer unten in den fünfzehn Feldern steht
	// und auf „Übersicht" wechselt, sähe sonst den kurzen Reiter von unten.
	const tabsRef = useRef(null)
	useScrollTopOnTabChange(tab, tabsRef)

	// Die Zeile kommt beim Öffnen mit — daraus stehen Name und Junior Company
	// sofort im Kopf, noch bevor die Antwort da ist.
	const oeffne = row => {
		setZeile(row)
		setDaten(null)
		// Jede Junior Company beginnt bei der Übersicht: Der Reiter, in dem man
		// das Fenster zuletzt verlassen hat, ist für die nächste Zeile keine
		// Aussage.
		setTab(TAB.UEBERSICHT)

		if (!row?.companyId) return

		setLaedt(true)
		http.get(TApi.ADMIN_CONCEPT_COMPANY(row.companyId))
			.then(({ data }) => setDaten(data))
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setLaedt(false))
	}

	/*
	 * Das Konzept zum Mitnehmen. Der Server setzt den Dateinamen selbst; hier
	 * steht er noch einmal, weil ein Blob im Browser keinen Namen hat — was in
	 * der Kopfzeile stand, ist beim Speichern-Fenster schon vergessen.
	 */
	const holePdf = () => {
		if (!zeile?.companyId) return

		setHoltPdf(true)
		http.get(TApi.ADMIN_CONCEPT_COMPANY_PDF(zeile.companyId), { responseType: 'arraybuffer' })
			.then(({ data }) => {
				downloadFile(
					exportFileName(`Skills4Retail-Konzept ${zeile.companyName || ''}`, 'pdf'),
					new Blob([data], { type: blobTypes.PDF })
				)
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setHoltPdf(false))
	}

	const person = daten?.students?.find(student => student.id === zeile?.id) || null
	const kapitel = daten?.config?.chapters || 10

	/*
	 * Was wann geschehen ist — als fertige Sätze unter dem Stand.
	 *
	 * Sie stehen hier und nicht in `ConceptOverview`, weil sie ein Datum
	 * formatieren: `datum()` rechnet in Wiener Zeit (`shared/datetime`), und
	 * diese Umrechnung soll im Fenster an einer einzigen Stelle stehen.
	 */
	const hinweise = daten
		? [
				daten.submittedAt &&
					`Abgeschlossen am ${datum(daten.submittedAt)}${
						daten.submittedByName ? ` von ${daten.submittedByName}` : ''
					}.`,
				daten.status === 'RETURNED' &&
					daten.returnedAt &&
					`Zurückgegeben am ${datum(daten.returnedAt)}${
						daten.returnedByName ? ` von ${daten.returnedByName}` : ''
					}.${daten.returnReason ? ` Hinweis: „${daten.returnReason}“` : ''}`,
				daten.scoredAt && `Bewertet am ${datum(daten.scoredAt)}.`,
				daten.waivedAt &&
					`Im Störfall freigegeben am ${datum(daten.waivedAt)}${
						daten.waiveReason ? `: „${daten.waiveReason}“` : '.'
					}`
		  ].filter(Boolean)
		: []

	/*
	 * Die Zahl am Reiter „Schüler:innen" ist die der Junior Company, nicht die
	 * der Bewertung: Wer sie liest, will wissen, wie viele dahinterstehen.
	 */
	const tabs = [
		{ key: TAB.UEBERSICHT, label: 'Übersicht' },
		{ key: TAB.SCHUELER, label: 'Schüler:innen', count: daten?.students?.length || 0 },
		{ key: TAB.KONZEPT, label: CONCEPT_NAME, count: daten?.fields?.length || 0 },
		{ key: TAB.COMPANY, label: 'Die Junior Company' }
	]

	return (
		<BaseModal
			fitViewport
			maxWidth={980}
			// Welches Konzept — nicht nur, dass es eines ist: Skills4Retail ist
			// das Programm, „Marketing & E-Commerce" das Thema dieses Konzepts.
			title={`Skills4Retail-Konzept: ${CONCEPT_NAME}`}
			nameEvent={types.openConceptDetail}
			onOpen={oeffne}
			onClose={() => {
				setZeile(null)
				setDaten(null)
			}}
		>
			{() => (
				<div className={styles.root}>
					{/*
					 * Der Kopf trägt nur noch die Zuordnung: welche Junior Company und
					 * über welche Zeile geöffnet. Alles, was früher hier stand
					 * (Unternehmensform, Schule, Lehrkraft, Kapitel, Score), hat jetzt
					 * seinen Reiter — im Kopf stünde es beim Lesen jedes Reiters noch
					 * einmal.
					 */}
					<header className={styles.head}>
						<div>
							<span className={styles.headLabel}>Junior Company</span>
							<h4 className={styles.headTitle}>{zeile?.companyName || '—'}</h4>
						</div>
						<div className={styles.headPerson}>
							<span className={styles.headLabel}>Geöffnet über</span>
							<b>{zeile?.name}</b>
						</div>
					</header>

					{!zeile?.companyId && (
						<p className={styles.empty}>
							Diese Schüler:in gehört zu keiner Junior Company — ohne Junior Company gibt es kein Konzept.
						</p>
					)}

					{laedt && <Loading />}

					{daten && (
						<>
							<TabBar
								className={styles.tabs}
								tabs={tabs}
								active={tab}
								onChange={setTab}
								tabsRef={tabsRef}
							/>

							{/* ---------- Übersicht ---------- */}

							{tab === TAB.UEBERSICHT && (
								<div className={styles.panel}>
									{/*
									 * Der Überblick steht im ersten Reiter: Wer das Fenster
									 * öffnet, will zuerst wissen, wo die Junior Company im
									 * Ablauf steht und wer als Nächstes am Zug ist. Die
									 * anderen Reiter beantworten erst die zweite Frage.
									 *
									 * Die Verwaltung bekommt zum Abschluss einer Lernstrecke
									 * bewusst keine Mitteilung (das wären bei über tausend
									 * Schüler:innen hunderte im Monat); sie sieht den Stand
									 * hier, wenn sie ihn braucht.
									 */}
									<ConceptOverview
										daten={daten}
										status={daten.status}
										statusText={
											daten.started ? STATUS_TEXT[daten.status] || daten.status : NICHT_BEGONNEN
										}
										hinweise={hinweise}
									/>

									<section className={styles.block}>
										<h5 className={styles.blockTitle}>Bewertung durch die Lehrkraft</h5>

										{!daten.scoring.fieldsScored ? (
											/*
											 * „Sobald die Schüler:innen abgeschlossen haben" stand hier
											 * bisher ohne Gegenstand: abgeschlossen — was? Der Satz
											 * nennt jetzt die Bedingung und verweist auf die
											 * Schrittliste darüber, wo steht, welcher der Schritte
											 * offen ist.
											 */
											<p className={styles.blockHint}>
												Die Lehrkraft hat bisher keines der {daten.scoring.fieldsTotal}{' '}
												Konzeptfelder bewertet.
												{daten.state?.applies === false
													? ' Für diese Unternehmensform wird das Konzept zurzeit nicht ' +
													  'verlangt — sie muss es also auch nicht bewerten.'
													: daten.status === 'SUBMITTED'
													? ' Das Konzept ist abgeschlossen und liegt bei ihr.'
													: ' Bewerten kann sie erst, wenn die Schüler:innen das Konzept zur ' +
													  'Bewertung abschließen — dafür müssen alle ' +
													  `${daten.scoring.fieldsTotal} Konzeptfelder befüllt sein, jede:r ` +
													  'Schüler:in mindestens einem Feld zugeteilt und die Lernstrecke im ' +
													  'verlangten Umfang abgeschlossen. Was davon noch fehlt, steht in ' +
													  'der Schrittliste darüber.'}
											</p>
										) : (
											<>
												<div className={styles.scoreCards}>
													<div className={styles.scoreCard}>
														<span className={styles.scoreLabel}>
															Bewertete Konzeptfelder
														</span>
														<span className={styles.scoreValue}>
															{daten.scoring.fieldsScored} / {daten.scoring.fieldsTotal}
														</span>
													</div>
													<div className={styles.scoreCard}>
														<span className={styles.scoreLabel}>Vergebene Punkte</span>
														<span className={styles.scoreValue}>
															{daten.scoring.points} / {daten.scoring.max}
														</span>
													</div>
													<div className={styles.scoreCard}>
														<span className={styles.scoreLabel}>
															Score der Junior Company
														</span>
														<span className={styles.scoreValue}>
															{daten.scoring.percent} %
														</span>
													</div>
												</div>

												<p className={styles.blockHint}>
													{daten.scoring.scoredByNames.length
														? `Bewertet von ${daten.scoring.scoredByNames.join(', ')}`
														: 'Bewertet'}
													{daten.scoring.firstScoredAt
														? datum(daten.scoring.firstScoredAt) ===
														  datum(daten.scoring.lastScoredAt)
															? ` am ${datum(daten.scoring.firstScoredAt)}`
															: ` zwischen dem ${datum(
																	daten.scoring.firstScoredAt
															  )} und dem ${datum(daten.scoring.lastScoredAt)}`
														: ''}
													. Unbewertete Konzeptfelder zählen weder oben noch unten mit — der
													Score misst also das bisher Bewertete, nicht das Ganze. Wie sich das
													auf die Schüler:innen verteilt, steht im Reiter „Schüler:innen".
												</p>
											</>
										)}
									</section>
								</div>
							)}

							{/* ---------- Schüler:innen ---------- */}

							{tab === TAB.SCHUELER && (
								<div className={styles.panel}>
									{/*
									 * Die angeklickte Person zuerst: Sie ist der Anlass, aus dem
									 * das Fenster offen steht. Ihre beiden Zahlen (Kapitel,
									 * Score) standen früher im Kopf und stehen jetzt dort, wo
									 * es um Personen geht.
									 */}
									<section className={styles.block}>
										<h5 className={styles.blockTitle}>Lernstrecke von {zeile?.name}</h5>
										<p className={styles.blockHint}>
											{person?.exempt
												? 'Diese Schüler:in ist von der Quizpflicht befreit'
												: `${zeile?.chaptersPassed ?? 0} von ${kapitel} Kapiteln bestanden`}
											{person?.exempt && person.exemptReason ? `: „${person.exemptReason}“` : '.'}{' '}
											{zeile?.scorePercent === null || zeile?.scorePercent === undefined
												? 'Noch kein Score — es ist keines ihrer Felder bewertet.'
												: `Score ${zeile.scorePercent} % aus ${zeile.scorePoints} von ${zeile.scoreMax} Punkten.`}
										</p>

										{person ? (
											<>
												<p className={styles.blockHint}>
													Zehn Kapitel mit je {daten.config.questionsPerChapter} Fragen.
													Bestanden ist ein Kapitel ab {daten.config.quizPass} richtigen
													Antworten.
												</p>
												<ul className={styles.chapters}>
													{Array.from({ length: kapitel }, (_, index) => index + 1).map(
														no => {
															const stand = person.chapters.find(
																eintrag => eintrag.chapter === no
															)

															return (
																<li
																	key={no}
																	className={clsx(
																		styles.chapter,
																		stand?.passed && styles.chapterPassed,
																		stand && !stand.passed && styles.chapterOpen
																	)}
																>
																	<span className={styles.chapterNo}>{no}</span>
																	<span className={styles.chapterScore}>
																		{stand
																			? `${stand.correct}/${stand.total}`
																			: '—'}
																	</span>
																</li>
															)
														}
													)}
												</ul>
											</>
										) : (
											<p className={styles.blockHint}>
												Diese Schüler:in steht nicht (mehr) in der Junior Company — ihre Kapitel
												stehen deshalb hier nicht.
											</p>
										)}
									</section>

									<section className={styles.block}>
										<h5 className={styles.blockTitle}>Alle Schüler:innen der Junior Company</h5>
										{/*
										 * Dieselbe Auskunft wie bei Schüler:innen und Lehrkraft: wie viele
										 * Abschlüsse nötig sind und wie viele erfüllt. Ohne sie lässt sich
										 * aus der Liste darunter nicht ablesen, ob die Junior Company
										 * hängt — die nötige Zahl steht in den Einstellungen und ist je
										 * Unternehmensform verschieden.
										 */}
										<CourseStanding
											state={daten.state}
											students={daten.students}
											config={daten.config}
											ohneListe
										/>
										<ul className={styles.people}>
											{daten.students.map(student => (
												<li
													key={student.id}
													className={clsx(
														styles.person,
														student.id === zeile?.id && styles.personCurrent
													)}
												>
													<span className={styles.personName}>{student.name}</span>
													<span className={styles.personMeta}>
														{student.exempt
															? 'befreit'
															: `${student.chaptersPassed} von ${kapitel} Kapiteln`}
													</span>
												</li>
											))}
											{!daten.students.length && (
												<li className={styles.person}>
													Diese Junior Company hat noch keine Schüler:innen.
												</li>
											)}
										</ul>
									</section>

									{/*
									 * Wer an welchen Feldern arbeitet — und was davon bewertet
									 * ist. Die Aufschlüsselung stand früher erst da, wenn die
									 * Lehrkraft schon bewertet hatte; die Zuteilung selbst ist
									 * aber vorher genauso eine Auskunft, und in diesem Reiter
									 * ist sie die eigentliche Frage.
									 */}
									{!!daten.scoring.students.length && (
										<section className={styles.block}>
											<h5 className={styles.blockTitle}>Zuteilung und Bewertung je Schüler:in</h5>
											<div className={styles.scoreScroll}>
												<table className={styles.scoreTable}>
													<thead>
														<tr>
															<th>Schüler:in</th>
															<th>Zugeteilte Konzeptfelder</th>
															<th>Davon bewertet</th>
															<th>Punkte</th>
															<th>Score</th>
														</tr>
													</thead>
													<tbody>
														{daten.scoring.students.map(student => (
															<tr
																key={student.id}
																className={clsx(
																	student.id === zeile?.id && styles.scoreRowCurrent
																)}
															>
																<td>{student.name}</td>
																<td>
																	{student.assigned.length
																		? `${student.assigned.length} (${alsBereiche(
																				student.assigned
																		  )})`
																		: 'keine'}
																</td>
																<td>
																	{student.assigned.length
																		? `${student.scored.length} von ${student.assigned.length}`
																		: '—'}
																</td>
																<td>
																	{student.points === null
																		? '—'
																		: `${student.points} / ${student.max}`}
																</td>
																<td>
																	{student.percent === null
																		? '—'
																		: `${student.percent} %`}
																</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>

											<p className={styles.blockHint}>
												Der Score einer Schüler:in ist keine Note für die Person, sondern eine
												Auskunft darüber, wie die Konzeptfelder bewertet wurden, an denen sie
												gearbeitet hat. Die Nummern verweisen auf die fünfzehn Felder im Reiter
												„{CONCEPT_NAME}".
											</p>
										</section>
									)}
								</div>
							)}

							{/* ---------- Die fünfzehn Konzeptfelder ---------- */}

							{tab === TAB.KONZEPT && (
								<div className={styles.panel}>
									<section className={styles.block}>
										<h5 className={styles.blockTitle}>Die fünfzehn Konzeptfelder</h5>
										<p className={styles.blockHint}>
											Der Text stammt von der Junior Company, die Punkte von der betreuenden
											Lehrkraft. Hier lässt sich beides nur lesen — bewertet wird im Bereich der
											Lehrkraft.
											{daten.started ? '' : ' Geschrieben hat die Junior Company bisher nichts.'}
										</p>

										{/*
										 * Der Knopf steht in diesem Reiter: Das PDF enthält genau
										 * das, was darunter steht — die fünfzehn Felder mit Text
										 * und Punkten.
										 */}
										{/* Alumni (#213): lesen ja, kein PDF zum Mitnehmen. */}
										{!readOnly && (
											<div className={styles.actions}>
												<span>
													Das ganze Konzept mit allen fünfzehn Feldern herunterladen als
												</span>
												{holtPdf ? (
													<Icon
														className={styles.spin}
														size={{ w: 16, h: 16 }}
														name="loading"
														color="#22404D"
													/>
												) : (
													<Btn
														theme="download"
														icon="download"
														iconSize={{ w: 24, h: 24 }}
														onClick={holePdf}
														disabled={laedt || !daten}
													>
														PDF
													</Btn>
												)}
											</div>
										)}

										{daten.fields.map(field => (
											<div
												key={field.no}
												className={clsx(
													styles.field,
													(field.assigned || []).includes(zeile?.id) && styles.fieldOwn
												)}
											>
												{(field.assigned || []).includes(zeile?.id) && (
													<span className={styles.fieldMark}>
														Daran hat {zeile?.name} gearbeitet
													</span>
												)}
												<ScoreField readOnly field={field} students={daten.students} />
											</div>
										))}
									</section>
								</div>
							)}

							{/* ---------- Die Junior Company ---------- */}

							{tab === TAB.COMPANY && (
								<div className={styles.panel}>
									<section className={styles.block}>
										<h5 className={styles.blockTitle}>Junior Company und Schule</h5>
										<p className={styles.blockHint}>
											Dieselben Angaben, nach denen die Liste filtert und sortiert. Geändert
											werden sie im Unternehmensprofil, nicht hier.
										</p>
										<dl className={styles.facts}>
											<div className={styles.fact}>
												<dt>Junior Company</dt>
												<dd>{daten.company?.name || zeile?.companyName || '—'}</dd>
											</div>
											<div className={styles.fact}>
												<dt>Unternehmensform</dt>
												<dd>
													{daten.company?.companyType
														? mapCompanyTypeToName(daten.company.companyType)
														: '—'}
												</dd>
											</div>
											<div className={styles.fact}>
												{/*
												 * Nicht „Stand": So heißt im Reiter „Übersicht"
												 * der Stand des Konzepts. Hier geht es um die
												 * Freischaltung der Junior Company — dieselbe
												 * Unterscheidung wie in der Unternehmensliste.
												 */}
												<dt>Aktivierung</dt>
												<dd>
													{daten.company?.isActive ? 'aktiviert' : 'noch nicht aktiviert'}
												</dd>
											</div>
											<div className={styles.fact}>
												<dt>Schule</dt>
												<dd>{daten.company?.schoolName || zeile?.schoolName || '—'}</dd>
											</div>
											<div className={styles.fact}>
												<dt>Ort der Schule</dt>
												<dd>{daten.company?.schoolLocation || zeile?.schoolLocation || '—'}</dd>
											</div>
											<div className={styles.fact}>
												<dt>Bundesland</dt>
												<dd>
													{daten.company?.state
														? stateLabel(daten.company.state)
														: zeile?.state
														? stateLabel(zeile.state)
														: '—'}
												</dd>
											</div>
											<div className={styles.fact}>
												<dt>Betreuende Lehrkraft</dt>
												<dd>{daten.company?.teacherName || zeile?.teacherName || '—'}</dd>
											</div>
											<div className={styles.fact}>
												<dt>Schüler:innen</dt>
												<dd>{daten.students.length}</dd>
											</div>
										</dl>
									</section>

									<section className={styles.block}>
										<h5 className={styles.blockTitle}>
											Gilt das Konzept für diese Junior Company?
										</h5>
										<p className={styles.blockHint}>
											{daten.state?.applies === false
												? 'Nein — für diese Unternehmensform wird das Konzept zurzeit nicht ' +
												  'verlangt. Die Junior Company kann es trotzdem schreiben; es hält ' +
												  'nichts auf. Ob es verlangt wird, steht in den Einstellungen unter ' +
												  '„Checkliste & Voraussetzungen".'
												: 'Ja — für diese Unternehmensform wird das Konzept verlangt. Was es ' +
												  'aufhält, wenn es fehlt, steht in den Einstellungen unter ' +
												  '„Checkliste & Voraussetzungen".'}
										</p>
									</section>
								</div>
							)}
						</>
					)}
				</div>
			)}
		</BaseModal>
	)
}
